// src/pages/app/ReportsPage.jsx — Analytics & Reporting
import { useEffect, useState } from 'react';
import { reportsApi } from '@/lib/api';
import { toast } from 'sonner';
import { BarChart3, TrendingUp, AlertTriangle, Download, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

const TABS = ['spending', 'violations', 'advances'];

export default function ReportsPage() {
  const [tab, setTab] = useState('spending');
  const [spending, setSpending] = useState([]);
  const [violations, setViolations] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ from_date: '', to_date: '', department: '' });

  const loadSpending = async () => {
    try {
      const { data } = await reportsApi.spending(filters);
      setSpending(data);
    } catch { toast.error('Failed to load spending report'); }
  };

  const loadViolations = async () => {
    try {
      const { data } = await reportsApi.violations();
      setViolations(data);
    } catch { toast.error('Failed to load violations report'); }
  };

  const loadAdvances = async () => {
    try {
      const { data } = await reportsApi.advances();
      setAdvances(data);
    } catch { toast.error('Failed to load advances report'); }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadSpending(), loadViolations(), loadAdvances()]);
      setLoading(false);
    };
    load();
  }, []);

  const handleFilterApply = () => loadSpending();

  // Chart data
  const deptSpendData = spending.reduce((acc, row) => {
    const dept = row.department || 'Unknown';
    const existing = acc.find(a => a.department === dept);
    if (existing) existing.total += parseFloat(row.total_spend || 0);
    else acc.push({ department: dept, total: parseFloat(row.total_spend || 0) });
    return acc;
  }, []).sort((a, b) => b.total - a.total).slice(0, 8);

  const violationByCategory = violations.reduce((acc, v) => {
    const cat = v.category || 'other';
    const existing = acc.find(a => a.category === cat);
    if (existing) existing.count++;
    else acc.push({ category: cat, count: 1 });
    return acc;
  }, []);

  const totalSpend = deptSpendData.reduce((s, d) => s + d.total, 0);
  const overdueAdvances = advances.filter(a => a.days_outstanding > 30);

  const exportCSV = (data, filename) => {
    if (!data.length) return toast.error('No data to export');
    const keys = Object.keys(data[0]);
    const csv = [keys.join(','), ...data.map(row => keys.map(k => JSON.stringify(row[k] ?? '')).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900">Reports & Analytics</h1>
          <p className="text-slate-500 text-sm mt-1">Spend insights, policy violations, and advance aging</p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-3 sm:p-4 lg:p-5">
            <p className="text-xs text-slate-400 uppercase tracking-wide">Total Dept Spend</p>
            <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">₹{totalSpend.toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4 lg:p-5">
            <p className="text-xs text-slate-400 uppercase tracking-wide">Policy Violations</p>
            <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">{violations.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4 lg:p-5">
            <p className="text-xs text-slate-400 uppercase tracking-wide">Overdue Advances (30d+)</p>
            <p className={cn("text-xl sm:text-2xl font-bold mt-1", overdueAdvances.length > 0 ? "text-red-600" : "text-slate-900")}>
              {overdueAdvances.length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={cn("px-4 py-1.5 rounded-md text-sm font-medium transition-colors capitalize",
              tab === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}>
            {t === 'spending' ? 'Spend by Dept' : t === 'violations' ? 'Policy Violations' : 'Advance Aging'}
          </button>
        ))}
      </div>

      {/* Spending Tab */}
      {tab === 'spending' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm sm:text-base">Department Spend Breakdown</CardTitle>
                <button onClick={() => exportCSV(spending, 'spending_report.csv')}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3 mb-4 flex-wrap">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">From</label>
                  <input type="date" value={filters.from_date} onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">To</label>
                  <input type="date" value={filters.to_date} onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm focus:outline-none" />
                </div>
                <div className="self-end">
                  <button onClick={handleFilterApply} className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
                    <Filter className="w-3.5 h-3.5" />Apply
                  </button>
                </div>
              </div>
              {loading ? (
                <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" /></div>
              ) : !deptSpendData.length ? (
                <p className="text-center text-slate-400 py-12">No settled expenses in this period</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={deptSpendData} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="department" tick={{ fontSize: 11, fill: '#94a3b8' }} angle={-30} textAnchor="end" />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Spend']} />
                    <Bar dataKey="total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Violations Tab */}
      {tab === 'violations' && (
        <div className="space-y-4">
          {violationByCategory.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm sm:text-base">Violations by Category</CardTitle>
                  <button onClick={() => exportCSV(violations, 'violations_report.csv')}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
                    <Download className="w-3.5 h-3.5" /> Export CSV
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={violationByCategory} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={90} label={({ category, count }) => `${category} (${count})`}>
                      {violationByCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
          <Card className="border-slate-200 bg-white overflow-hidden shadow-sm">
            <CardContent className="p-0">
              {!violations.length ? (
                <p className="text-center text-slate-400 py-12">No policy violations — great!</p>
              ) : (
                <div className="overflow-x-auto">
                  <div className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between text-xs text-slate-500 font-medium">
                    <span>Showing <strong>{violations.length}</strong> policy violation{violations.length === 1 ? '' : 's'}</span>
                  </div>
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="bg-slate-50/80 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        {['Employee', 'Dept & Grade', 'Category', 'Amount', 'Date', 'Flag Reason', 'Claim Status'].map(h => (
                          <th key={h} className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {violations.map((v, i) => (
                        <tr key={i} className="hover:bg-slate-50/80 transition-colors group">
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900">{v.employee_name}</td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-xs text-slate-600 font-medium">{v.department} ({v.grade})</td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 capitalize text-xs text-slate-700 font-medium">{v.category?.replace('_', ' ')}</td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900 whitespace-nowrap">₹{parseFloat(v.amount).toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-xs text-slate-600 whitespace-nowrap">{v.expense_date ? new Date(v.expense_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-rose-700 text-xs font-semibold">{v.flag_reason}</td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 whitespace-nowrap">
                            <span className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-medium border border-slate-200 capitalize">{v.claim_status?.replace('_', ' ')}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Advances Aging Tab */}
      {tab === 'advances' && (
        <Card className="border-slate-200 bg-white overflow-hidden shadow-sm">
          <CardHeader className="py-3.5 px-6 border-b bg-slate-50/50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm sm:text-base font-bold text-slate-900">Outstanding Advances (Approved, Unreconciled)</CardTitle>
              <button onClick={() => exportCSV(advances, 'advances_aging.csv')}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 transition-colors">
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {!advances.length ? (
              <p className="text-center text-slate-400 py-12">No outstanding advances</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="bg-slate-50/80 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {['Employee', 'Email', 'Department', 'Amount', 'Approved On', 'Days Outstanding', 'Trip'].map((h, i) => (
                        <th key={h} className={cn("px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3", i === 6 ? "text-right" : "")}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {advances.map(a => (
                      <tr key={a.id} className="hover:bg-slate-50/80 transition-colors group">
                        <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900">{a.employee_name}</td>
                        <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-slate-500 text-xs">{a.employee_email}</td>
                        <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-slate-500">{a.department || '—'}</td>
                        <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900">₹{parseFloat(a.approved_amount || 0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-slate-500">{a.approved_at ? new Date(a.approved_at).toLocaleDateString('en-IN') : '—'}</td>
                        <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">
                          <span className={cn("text-xs px-2 py-0.5 rounded-full font-semibold",
                            a.days_outstanding > 30 ? "bg-red-100 text-red-700" : a.days_outstanding > 15 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700")}>
                            {Math.round(a.days_outstanding || 0)} days
                          </span>
                        </td>
                        <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-slate-500 text-xs">{a.trip_destination || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
